import { dispatch_integration_types } from '@/components/MyOrders/filterOptions';
import { GENERAL_ELECTRIC } from '@/constants/integrationTypes';

export const FUEL_TYPE_OPTIONS = [
    { value: 'LOCAL_PADD', label: 'Local' },
    { value: 'NATIONAL_PADD', label: 'National' },
];

// clients.carrier_type — see migration 1777613055039_self_serve.
export const CARRIER_TYPE_LABELS = {
    AGENT: 'Agent',
    FLEX: 'Flex',
    LTL: 'LTL',
    GIG: 'Gig',
    PARTNER: 'Partner',
};

export const SHIPPER_TYPE_LABELS = {
    MARKETPLACE: 'Marketplace',
    AUTO_ASSIGN: 'Auto Assign',
    RATE_SHOP: 'Rate Shop',
};

// Display labels for pricing_overrides.marketplace_tier. T2_INTEGRATIONS_ONLY is a
// legacy value still possible on existing rows — render it as "Tier 2" but don't
// surface it as a pickable option (T2_INTEGRATIONS_BILLING is the canonical Tier 2).
export const MARKETPLACE_TIER_LABELS = {
    UNKNOWN: 'Unknown',
    T1_INTEGRATIONS_ONLY: 'Tier 1',
    T2_INTEGRATIONS_BILLING: 'Tier 2',
    T2_INTEGRATIONS_ONLY: 'Tier 2',
    T3_FULLY_MANAGED: 'Tier 3',
};

export const MARKETPLACE_TIER_OPTIONS = [
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'T1_INTEGRATIONS_ONLY', label: 'Tier 1' },
    { value: 'T2_INTEGRATIONS_BILLING', label: 'Tier 2' },
    { value: 'T3_FULLY_MANAGED', label: 'Tier 3' },
];

// clients.supply_chain_legs (public.supply_chain_leg enum) — see migration 1777613055039_self_serve.
// Display labels intentionally diverge from the enum names (e.g. MIDDLE_MILE renders as "First")
// to match the operator-facing terminology in the Figma; don't infer the mapping from the names.
export const SUPPLY_CHAIN_LEG_LABELS = {
    FINAL_MILE: 'Final',
    MIDDLE_MILE: 'First',
    E2E: 'Full',
    WAREHOUSE: 'Crossdock',
    GIG: 'Gig',
};

export const INTEGRATION_TYPE_LABELS = {
    ...Object.fromEntries(dispatch_integration_types.map(({ value, label }) => [value, label])),
    // PC Miler is shipper-scoped (Account Details → Mileage), not a dispatch integration, so it's
    // intentionally absent from dispatch_integration_types; its label is declared here for display.
    PC_MILER: 'PC Miler',
    // Custom Global is first-leg LTL and full-mile / last-mile (same as CEVA / Metro).
    CUSTOMCO: 'Custom Global',
};

// integration_overrides.type values that are shipper-scoped and configured through a dedicated
// surface (Account Details → Mileage) rather than as carrier/marketplace integrations. Excluded
// from the generic carrier-setup dropdown, and by buildCarrierAssetMaps from the carrier asset
// maps (otherwise they'd surface/count as marketplace integrations).
export const SHIPPER_SCOPED_OVERRIDE_TYPES = ['PC_MILER'];

// Each entry defines a chip. `events` is an array — the chip highlights if any of its
// event keys are present in the integration's subscriptions array.
export const SUBSCRIBED_EVENT_CHIPS = [
    { label: 'Agent Dispatched', events: ['carrier_assigned'] },
    { label: 'Order Cancelled', events: ['order_cancelled'] },
    { label: 'Order Updated', events: ['order_details_updated'] },
    { label: 'Reschedule Requested', events: ['order_scheduled', 'order_rescheduled'] },
];

// Each entry describes the editable fields for a given integration type.
// secret: true fields display a lock icon and are read-only in the UI.
export const INTEGRATION_CONFIG_FIELDS = {
    [GENERAL_ELECTRIC]: [
        { key: 'manufacturer', label: 'Manufacturer', target: 'default_order' },
        { key: 'po_duplicate_override', label: 'PO Duplicate Override', target: 'default_order' },
    ],
    DISPATCHTRACK: [
        { key: 'company', label: 'Company' },
        { key: 'account', label: 'Account' },
        { key: 'code', label: 'Code' },
        { key: 'baseUrl', label: 'Base URL' },
        { key: 'api_key', label: 'API Key', secret: true, suffix: 'API_KEY' },
    ],
    GRASSHOPPER: [
        { key: 'base_url', label: 'Base URL' },
        { key: 'username', label: 'Username' },
        { key: 'identifier', label: 'Identifier' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
    ],
    FLEETENABLE: [
        { key: 'username', label: 'Username' },
        { key: 'password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        {
            key: 'customer_identifier',
            label: 'Customer Identifier',
            tooltip: 'FleetEnable-issued. Same as shipper code unless they give you a different value.',
        },
        { key: 'shipper_code', label: 'Shipper Code' },
        { key: 'carrier_code', label: 'Carrier Code' },
    ],
    XCELERATOR_AXIS: [
        { key: 'baseUrl', label: 'Base URL' },
        {
            key: 'company',
            label: 'Company',
            tooltip: 'Triggers custom code for more complex customizations for this carrier/shipper combo',
        },
        { key: 'account_no', label: 'Account No' },
        { key: 'xcelerator_username', label: 'Username' },
        { key: 'xcelerator_password', label: 'Password', secret: true, suffix: 'PASSWORD' },
        { key: 'modifications.serviceId', label: 'Service Id', type: 'integer', path: ['modifications', 'serviceId'] },
        { key: 'modifications.vehicleId', label: 'Vehicle Id', type: 'integer', path: ['modifications', 'vehicleId'] },
        {
            key: 'modifications.packageIdMapping.default',
            label: 'Package Id',
            type: 'integer',
            path: ['modifications', 'packageIdMapping', 'default'],
        },
        {
            key: 'modifications.packageIdMapping.pallet',
            label: 'Pallet Id',
            type: 'integer',
            path: ['modifications', 'packageIdMapping', 'pallet'],
        },
    ],
    FRAYT: [
        { key: 'user_id', label: 'User ID' },
        { key: 'client_id', label: 'Client ID' },
        { key: 'secret_key', label: 'Secret Key', secret: true, suffix: 'SECRET_KEY' },
    ],
    BUNGII: [
        { key: 'app_id', label: 'App ID' },
        { key: 'api_key', label: 'API Key', secret: true, suffix: 'API_KEY' },
        { key: 'client_id', label: 'Client ID' },
        { key: 'secret_key', label: 'Secret Key', secret: true, suffix: 'SECRET_KEY' },
        { key: 'scope_prefix', label: 'Scope Prefix' },
        { key: 'tracking_url', label: 'Tracking URL' },
    ],
    DISPATCHIT: [
        { key: 'secret_name', label: 'Secret Name', secret: true, suffix: 'SECRET' },
        { key: 'api_key_name', label: 'API Key Name', secret: true, suffix: 'API_KEY' },
    ],
    IDLS: [
        { key: 'type', label: 'Type' },
        { key: 'brand', label: 'Brand' },
        { key: 'base_url', label: 'Base URL' },
        { key: 'secret_key', label: 'Secret Key', secret: true, suffix: 'SECRET_KEY' },
    ],
    PC_MILER: [
        { key: 'api_key', label: 'API Key', secret: true, suffix: 'PCM_API_KEY' },
        { key: 'version', label: 'PC Miler Version', prefix: 'PCM', tooltip: 'Enter just the version number, e.g. 31' },
        { key: 'order_mileage_enabled', label: 'Use for order mileage calculations', type: 'boolean' },
    ],
    CUSTOMCO: [
        { key: 'user_key', label: 'API User', secret: true, suffix: 'API_USER' },
        { key: 'pass_key', label: 'API Password', secret: true, suffix: 'API_PASS' },
    ],
    CXT_API: [
        { key: 'client_id', label: 'Username' },
        { key: 'secret_key', label: 'Secret Key', secret: true, suffix: 'SECRET_KEY' },
        { key: 'base_url', label: 'Base URL' },
        { key: 'cust_id', label: 'Customer ID' },
        { key: 'order_type_id', label: 'Order Type ID', type: 'integer' },
        { key: 'service_type_id', label: 'Service Type ID', type: 'integer' },
        { key: 'tracking_url', label: 'Tracking URL' },
    ],
};
